import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import Header from '../Header/Header.tsx';
import Config from '../../../lib/Config.ts';
import { GetReservation } from '../../../lib/types.ts';
import {
  PaymentMethodsAtom,
  claimPaymentMethodSelection,
  reloadPaymentMethodsAtom,
  ReservationAtom,
} from '../lib/state.ts';
import PaymentPicker from './PaymentMethods.tsx';
import RemainingTime from '../RemainingTime/RemainingTime.tsx';
import { AuthAtom, useDirectLinkState } from '../lib/state.ts';
import {
  setPaymentMethod,
  setPaymentMethodForUnclaimedReservation,
  setupIntent,
  setupIntentForUnclaimedReservation,
  createPlaidLinkToken,
  createPlaidPaymentMethod,
} from '../../../lib/api.ts';
import {
  allowsAchPaymentType,
  allowsCardWalletPaymentType,
} from '../../Widget/lib/paymentTypes.ts';
import PlaidIcon from '../../../assets/cards/PlaidIcon.svg?react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
  Stripe,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';

import styles from './Payment.module.css';

type PaymentMethodTab = 'ach' | 'card';

const Payment: React.FC = () => {
  const reservation = useAtomValue(ReservationAtom);
  const [directLink, updateDirectLink] = useDirectLinkState();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const methods = useAtomValue(PaymentMethodsAtom);
  const auth = useAtomValue(AuthAtom);
  const reloadPaymentMethods = useSetAtom(reloadPaymentMethodsAtom);

  // ── Payment type detection ──────────────────────────────────────────────
  const supportsAch = allowsAchPaymentType(reservation?.allowedPaymentTypes);
  const supportsCard =
    !reservation?.allowedPaymentTypes?.length ||
    allowsCardWalletPaymentType(reservation?.allowedPaymentTypes);
  const hasBothMethods = supportsAch && supportsCard;

  // ── Tab state ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<PaymentMethodTab>(
    supportsAch ? 'ach' : 'card'
  );

  // ── ACH (Plaid) state ──────────────────────────────────────────────────
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
  const [achLinked, setAchLinked] = useState(false);
  const [achBankName, setAchBankName] = useState('');
  const [achLast4, setAchLast4] = useState('');

  // ── Card state (tracks whether a credit card has been added via Stripe) ─
  const [cardAdded, setCardAdded] = useState(false);
  const [cardBrand, setCardBrand] = useState('');
  const [cardLast4, setCardLast4] = useState('');

  useEffect(() => {
    const waitForIt = async () => {
      setStripe(await loadStripe(Config.data.stripe.key));
    };

    void waitForIt();
  }, []);

  // ── Fetch Plaid Link token when ACH is supported ───────────────────────
  useEffect(() => {
    if (!supportsAch || !auth?.token) return;
    const fetchToken = async () => {
      try {
        const response = await createPlaidLinkToken(auth.token);
        setPlaidLinkToken(
          response.create_link_token ?? response.link_token ?? null
        );
      } catch (e) {
        Sentry.captureException(e);
      }
    };
    void fetchToken();
  }, [supportsAch, auth?.token]);

  // ── Plaid Link handler ─────────────────────────────────────────────────
  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        const result = await createPlaidPaymentMethod(auth!.token, {
          public_token: publicToken,
          account_id: metadata.accounts[0]?.id,
        });
        setAchLinked(true);
        setAchBankName(metadata.institution?.name ?? '');
        setAchLast4(metadata.accounts[0]?.mask ?? '');

        updateDirectLink(claimPaymentMethodSelection(result.payment_method.id));
        reloadPaymentMethods();
      } catch (e) {
        Sentry.captureException(e);
      }
    },
  });

  const stripe_options: StripeElementsOptions = {
    mode: 'setup',
    currency: 'usd',
    appearance: {
      theme: 'stripe',

      variables: {
        fontFamily: 'Sailec',
        borderRadius: '10px',
        colorBackground: '#FFFFFF',
        spacingGridColumn: '30px',
        spacingGridRow: '30px',
      },

      rules: {
        '.Label': {
          fontSize: '16px',
          marginBottom: '15px',
          fontFamily: 'Sailec',
        },

        '.Input': {
          boxShadow: 'none',
          padding: '10px',
          fontSize: '16px',
          fontFamily: 'Sailec',
        },
      },
    },
    fonts: [
      {
        family: 'Sailec',
        src: 'url(https://book.dorsia.com/fonts/stripe-font.otf)',
      },
    ],
  };

  if (!reservation || !stripe) {
    return (
      <>
        <Header
          title="Sorry, issue with loading Reservation"
          step={2}
          show_image={false}
        />
      </>
    );
  }

  // Determine which tab content to show
  const showAchSection =
    activeTab === 'ach' || (!hasBothMethods && supportsAch);
  const showCardSection =
    activeTab === 'card' || (!hasBothMethods && supportsCard && !supportsAch);

  return (
    <>
      <Header
        title="Add payment method"
        step={2}
        show_image={false}
        isEvent={reservation.restaurant.type === 'event'}
      />

      {/* Always-mounted live region so the alert announces even when the guest
          lands here with the error already set [HOS-2029]. API card-failure
          messages are complete sentences (e.g. "Your card has expired."). */}
      <div
        role="alert"
        className={
          directLink?.payment_error ? clsx('content', styles.header_error) : undefined
        }
      >
        {directLink?.payment_error}
      </div>

      <div className="content">
        <h2>{reservation.restaurant?.name}</h2>

        <div className={styles.grid}>
          <div className="section">
            <h3>Reservation details</h3>

            <div>{reservation.reservation_at_tzd.format('ddd, MMMM D')}</div>
            <div>{reservation.reservation_at_tzd.format('h:mm a')}</div>
            <div>{reservation.party_size} guests</div>
            <div>{reservation.table_type}</div>
          </div>

          <div className="section">
            <h3>Your details</h3>

            <div>
              {reservation.user.first_name} {reservation.user.last_name}
            </div>
            <div>{reservation.user.email}</div>

            <div className={styles.edit}>
              <Link to={'../info'} className="lightButton">
                Edit your details
              </Link>
            </div>
          </div>
        </div>

        {/* ── Payment method toggle (ACH / Credit Card) ─────────────── */}
        {hasBothMethods && (
          <div
            style={{
              display: 'flex',
              background: '#F5F5F5',
              borderRadius: 8,
              padding: 4,
              marginBottom: 24,
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab('ach')}
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: activeTab === 'ach' ? '#fff' : 'transparent',
                fontWeight: activeTab === 'ach' ? 600 : 400,
                fontFamily: 'Sailec, sans-serif',
                fontSize: 14,
                boxShadow:
                  activeTab === 'ach' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              ACH
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('card')}
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: activeTab === 'card' ? '#fff' : 'transparent',
                fontWeight: activeTab === 'card' ? 600 : 400,
                fontFamily: 'Sailec, sans-serif',
                fontSize: 14,
                boxShadow:
                  activeTab === 'card'
                    ? '0 1px 3px rgba(0,0,0,0.08)'
                    : 'none',
              }}
            >
              Credit Card
            </button>
          </div>
        )}

        {/* ── ACH section ──────────────────────────────────────────── */}
        {showAchSection && (
          <div>
            {hasBothMethods && (
              <p
                style={{
                  fontSize: 13,
                  color: '#666',
                  marginBottom: 16,
                  lineHeight: 1.5,
                }}
              >
                For bookings over $10,000, we require a verified bank account
                via Plaid for payment and a credit card for incidentals.
              </p>
            )}

            {/* Bank Account card */}
            <div
              style={{
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                padding: 20,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                >
                  <PlaidIcon style={{ width: 20, height: 20, flexShrink: 0 }} />
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: 'Sailec, sans-serif',
                      }}
                    >
                      {achLinked
                        ? `${achBankName || 'Bank Account'} ····${achLast4}`
                        : 'Plaid Bank Account'}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#666',
                        marginTop: 2,
                      }}
                    >
                      Used to pay for this booking
                    </div>
                  </div>
                </div>
                <span
                  style={{
                    background: achLinked ? '#e8f5e9' : '#e8f5e9',
                    color: '#2e7d32',
                    fontSize: 11,
                    padding: '3px 8px',
                    borderRadius: 10,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {achLinked ? 'Linked' : 'Required'}
                </span>
              </div>
              {!achLinked && (
                <button
                  type="button"
                  onClick={() => plaidReady && openPlaid()}
                  disabled={!plaidReady}
                  style={{
                    width: '100%',
                    marginTop: 16,
                    padding: '12px 0',
                    borderRadius: 8,
                    border: 'none',
                    background: plaidReady ? '#000' : '#ccc',
                    color: '#fff',
                    cursor: plaidReady ? 'pointer' : 'not-allowed',
                    fontFamily: 'Sailec, sans-serif',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Link Bank Account
                </button>
              )}
            </div>

            {/* Credit Card for incidentals (shown alongside ACH when both supported) */}
            {hasBothMethods && (
              <div
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 12,
                  padding: 20,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                  >
                    <svg
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                      viewBox="0 0 32 32"
                      fill="none"
                    >
                      <rect
                        x="2"
                        y="6"
                        width="28"
                        height="20"
                        rx="2"
                        stroke="#0D0A06"
                        strokeWidth="1.5"
                      />
                      <path d="M2 12h28" stroke="#0D0A06" strokeWidth="1.5" />
                      <path
                        d="M7 18h6"
                        stroke="#0D0A06"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          fontFamily: 'Sailec, sans-serif',
                        }}
                      >
                        {cardAdded
                          ? `${cardBrand} ····${cardLast4}`
                          : 'Credit Card'}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          marginTop: 2,
                        }}
                      >
                        {cardAdded
                          ? 'Selected for incidentals'
                          : 'Used for incidentals only'}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      background: '#e8f5e9',
                      color: '#2e7d32',
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 10,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cardAdded ? 'Added' : 'Required'}
                  </span>
                </div>

                {cardAdded ? (
                  <button
                    type="button"
                    onClick={() => {
                      setCardAdded(false);
                      setCardBrand('');
                      setCardLast4('');
                    }}
                    style={{
                      marginTop: 8,
                      padding: 0,
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontFamily: 'Sailec, sans-serif',
                      fontSize: 13,
                      color: '#2e7d32',
                      fontWeight: 500,
                      textDecoration: 'underline',
                    }}
                  >
                    Change Card
                  </button>
                ) : (
                  <div style={{ marginTop: 16 }}>
                    {/* Show existing payment methods */}
                    {auth && methods.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <PaymentPicker reservation={reservation} />
                      </div>
                    )}

                    {/* Stripe form for new card */}
                    <Elements stripe={stripe} options={stripe_options}>
                      <IncidentalCardForm
                        reservation={reservation}
                        onCardAdded={(brand, last4) => {
                          setCardAdded(true);
                          setCardBrand(brand);
                          setCardLast4(last4);
                        }}
                      />
                    </Elements>
                  </div>
                )}
              </div>
            )}

            {/* For ACH-only (no card), show Next button after bank is linked */}
            {!hasBothMethods && achLinked && (
              <>
                <NextButton
                  reservation={reservation}
                  disabled={false}
                />
                <RemainingTime />
              </>
            )}

            {/* For dual methods: show Next when both are ready */}
            {hasBothMethods && achLinked && cardAdded && (
              <>
                <div style={{ marginTop: 24 }}>
                  <NextButton
                    reservation={reservation}
                    disabled={false}
                  />
                </div>
                <RemainingTime />
              </>
            )}

            {/* Show remaining time if no Next button is visible yet */}
            {(!achLinked || (hasBothMethods && !cardAdded)) && (
              <RemainingTime />
            )}
          </div>
        )}

        {/* ── Card section (existing behavior) ─────────────────────── */}
        {showCardSection && (
          <>
            {auth && methods.length > 0 && (
              <div className="section">
                <h3>Choose an existing payment method</h3>

                <PaymentPicker reservation={reservation} />
              </div>
            )}

            <div className="section">
              <h3>Enter your credit card</h3>

              {reservation.restaurant.type === 'event' ? null : (
                <p>
                  You will be charged upon booking. Tax, tip and any overages
                  are not included.
                </p>
              )}

              <Elements stripe={stripe} options={stripe_options}>
                <CheckoutForm reservation={reservation} />
              </Elements>
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ── NextButton (navigates to next step) ──────────────────────────────────

interface NextButtonProps {
  reservation: GetReservation;
  disabled: boolean;
}

const NextButton: React.FC<NextButtonProps> = ({ reservation, disabled }) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (reservation?.restaurant.type === 'event') {
      navigate('../confirm');
    } else {
      navigate('../101');
    }
  };

  return (
    <button className="nextButton" onClick={onClick} disabled={disabled}>
      Next
    </button>
  );
};

// ── IncidentalCardForm (Stripe form for incidentals in ACH+Card flow) ────

interface IncidentalCardFormProps {
  reservation: GetReservation;
  onCardAdded: (brand: string, last4: string) => void;
}

const IncidentalCardForm: React.FC<IncidentalCardFormProps> = ({
  reservation,
  onCardAdded,
}) => {
  const auth = useAtomValue(AuthAtom);
  const reloadPaymentMethods = useSetAtom(reloadPaymentMethodsAtom);
  const [directLink, updateDirectLink] = useDirectLinkState();
  const [booking, setBooking] = useState(false);
  const [name, setName] = useState(
    `${reservation.user.first_name} ${reservation.user.last_name}`
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    setName(`${reservation.user.first_name} ${reservation.user.last_name}`);
  }, [reservation.user.first_name, reservation.user.last_name]);

  const handleSubmit = async () => {
    setBooking(true);
    setErrorMessage(undefined);

    if (!stripe || !elements) return;

    const { error } = await elements.submit();
    if (error) {
      setErrorMessage(error.message);
      setBooking(false);
      return;
    }

    try {
      let response;
      if (auth?.token) {
        response = await setupIntent(auth.token);
      } else if (reservation?.uuid) {
        response = await setupIntentForUnclaimedReservation(reservation.uuid);
      }

      if (!response?.setupIntent) {
        Sentry.captureMessage(
          'DirectLink: Problem occurred with api/setup-intent (incidental card)'
        );
        setErrorMessage(
          'Sorry, there was an issue.  Please try again in a moment.'
        );
        setBooking(false);
        return;
      }

      const { setupIntent: result, error: confirmError } =
        await stripe.confirmSetup({
          elements,
          clientSecret: response.setupIntent.client_secret,
          confirmParams: {
            return_url: `${Config.data.uri.app}/reservation/${reservation.uuid}/confirm`,
            payment_method_data: {
              billing_details: {
                name,
                email: reservation.user.email,
              },
            },
          },
          redirect: 'if_required',
        });

      if (confirmError) {
        setErrorMessage(confirmError.message);
        setBooking(false);
        return;
      }

      const payment_method = result?.payment_method as string;
      let dorsia_payment;

      if (auth?.token) {
        dorsia_payment = await setPaymentMethod(auth.token, {
          paymentMethodId: payment_method,
        });
      } else if (reservation?.uuid) {
        dorsia_payment = await setPaymentMethodForUnclaimedReservation(
          reservation.uuid,
          { paymentMethodId: payment_method }
        );
      }

      // For incidentals card in dual flow, store the card but do NOT overwrite
      // the ACH payment method in claim_body — ACH is the primary charge method.
      // Only set claim_body.payment_method_id if no ACH method is already set.
      const existingAchPaymentId = directLink?.claim_body?.payment_method_id;
      const hasAchLinked = !!directLink?.ach_bank_last4;

      if (hasAchLinked && existingAchPaymentId) {
        // ACH is primary — store incidental card separately, preserve ACH in claim_body.
        // Deliberately not claimPaymentMethodSelection(): the charge method didn't change.
        updateDirectLink({
          incidental_card_id: dorsia_payment.payment_id,
        });
      } else {
        // No ACH — this card IS the primary payment method
        updateDirectLink(claimPaymentMethodSelection(dorsia_payment.payment_id));
      }

      reloadPaymentMethods();
      setBooking(false);

      // Extract card info from the Stripe result for display
      const brand = 'Card';
      const last4 = typeof result?.payment_method === 'string'
        ? result.payment_method.slice(-4)
        : '****';
      onCardAdded(brand, last4);
    } catch (e: unknown) {
      Sentry.captureException(e);
      setErrorMessage(
        'Sorry, there was an issue.  Please try again in a moment.'
      );
      setBooking(false);
    }
  };

  const payment_options: StripePaymentElementOptions = {
    defaultValues: {
      billingDetails: {
        name: `${reservation.user.first_name} ${reservation.user.last_name}`,
        email: reservation.user.email,
        phone: reservation.user.phone,
      },
    },
    fields: {
      billingDetails: {
        name: 'never',
      },
    },
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.field}>
          <label>Cardholder name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>

        <PaymentElement options={payment_options} />
      </form>

      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={!stripe || booking}
        style={{
          width: '100%',
          marginTop: 16,
          padding: '12px 0',
          borderRadius: 8,
          border: 'none',
          background: !stripe || booking ? '#ccc' : '#2e7d32',
          color: '#fff',
          cursor: !stripe || booking ? 'not-allowed' : 'pointer',
          fontFamily: 'Sailec, sans-serif',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {booking ? 'Adding...' : 'Add Credit Card'}
      </button>
    </>
  );
};

// ── CheckoutForm (original card-only flow) ───────────────────────────────

interface CheckoutProps {
  reservation: GetReservation;
}

const CheckoutForm: React.FC<CheckoutProps> = ({ reservation }) => {
  const auth = useAtomValue(AuthAtom);
  const reloadPaymentMethods = useSetAtom(reloadPaymentMethodsAtom);
  const [, updateDirectLink] = useDirectLinkState();
  const [booking, setBooking] = useState<boolean>(false);
  const [name, setName] = useState<string>(
    reservation
      ? `${reservation.user.first_name} ${reservation.user.last_name}`
      : ''
  );
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  // The page-level banner renders directLink.payment_error; this slot is for
  // form-level Stripe errors only, so don't seed it with the same message.
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    setName(`${reservation.user.first_name} ${reservation.user.last_name}`);
  }, [reservation.user.first_name, reservation.user.last_name]);

  const onName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.currentTarget.value);
  };

  const onSubmit = (event: React.SyntheticEvent) => {
    // es-lint is finicky about promises and event handlers
    event.preventDefault();
    void handleSubmit();
  };

  const onClick = (event: React.SyntheticEvent) => {
    // es-lint is finicky about promises and event handlers
    event.preventDefault();
    void handleSubmit();
  };

  const handleSubmit = async () => {
    setBooking(true);

    setErrorMessage(undefined);

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Trigger form validation and wallet collection
    const { error } = await elements.submit();

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(error.message);
      setBooking(false);
      return;
    }

    try {
      let response;

      if (auth?.token) {
        response = await setupIntent(auth!.token);
      } else if (reservation?.uuid) {
        response = await setupIntentForUnclaimedReservation(reservation.uuid);
      }

      if (!response?.setupIntent) {
        Sentry.captureMessage(
          'DirectLink: Problem occurred with api/setup-intent'
        );
        setErrorMessage(
          'Sorry, there was an issue.  Please try again in a moment.'
        );
        setBooking(false);
        return;
      }

      const { setupIntent: result, error } = await stripe.confirmSetup({
        elements,
        clientSecret: response.setupIntent.client_secret,
        confirmParams: {
          return_url: `${Config.data.uri.app}/reservation/${reservation.uuid}/confirm`, // FIXME: this won't necessarily work because we don't have a page to accept these params - but we don't have payment types that should require this
          payment_method_data: {
            billing_details: {
              name,
              email: reservation.user.email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message);
        setBooking(false);
        return;
      }

      const payment_method = result?.payment_method as string;
      let dorsia_payment;

      if (auth?.token) {
        dorsia_payment = await setPaymentMethod(auth.token, {
          paymentMethodId: payment_method,
        });
      } else if (reservation?.uuid) {
        dorsia_payment = await setPaymentMethodForUnclaimedReservation(
          reservation.uuid,
          {
            paymentMethodId: payment_method,
          }
        );
      }

      updateDirectLink(claimPaymentMethodSelection(dorsia_payment.payment_id));

      setBooking(false);
      reloadPaymentMethods();

      if (reservation?.restaurant.type === 'event') {
        navigate('../confirm');
      } else {
        navigate(`../101`);
      }
    } catch (e: unknown) {
      Sentry.captureException(e);
      setErrorMessage(
        'Sorry, there was an issue.  Please try again in a moment.'
      );
      setBooking(false);
    }
  };

  const payment_options: StripePaymentElementOptions = {
    defaultValues: {
      billingDetails: {
        name: `${reservation.user.first_name} ${reservation.user.last_name}`,
        email: reservation.user.email,
        phone: reservation.user.phone,
      },
    },
    fields: {
      billingDetails: {
        name: 'never',
      },
    },
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.field}>
          <label>Cardholder name</label>
          <input type="text" value={name} onChange={onName} />
        </div>

        <PaymentElement options={payment_options} />
      </form>

      <button
        className="nextButton"
        onClick={onClick}
        disabled={!stripe || booking}
      >
        Next
      </button>

      <RemainingTime />
    </>
  );
};

export default Payment;
